import { BehavioralMentalStatus } from "@/features/note-content/components/BehavioralMentalStatus";
import { BehavioralSymptomTrend } from "@/features/note-content/components/BehavioralSymptomTrend";
import CardSecondary from "@/components/UI/Card/CardSecondary";
import dayjs from "@/lib/helpers/dayjs";
import { ExpandingSelect } from "@/components/UI/ExpandingSelect";
import { GenericMultiselect } from "@/features/note-content/components/GenericMultiselect";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { IndividualSymptomTrend } from "@/features/note-content/components/IndividualSymptomTrend";
import { makeCnaiProps, makeCnaiTemplateProps } from "@/lib/helpers/clinically-ai";
import { PrescribeLabFindings } from "@/features/note-content/components/PrescribeLabFindings";
import { PrescriberMedicationsV2 } from "@/features/note-content/components/(medications)/components/PrescriberMedicationsV2";

import { DischargeContent } from "./(discharge)/DischargeContent";
import { GenericSectionNotes } from "./components/GenericSectionNotes";
import MemberProfileNotesDuration from "./components/MemberProfileNotesDuration";
import { NotesDiagnosis } from "./components/NotesDiagnosis";
import { PrescribeDiagnosisProgress } from "./components/PrescribeDiagnosisProgress";

export function PrescribeNewNote({
  editable,
  editHandler,
  errors,
  isCallManuallyLinked,
  note,
  profile,
  views,
}) {
  let { createEditView, profileView } = views;

  let medications = (
    <PrescriberMedicationsV2
      editable={editable}
      editHandler={editHandler}
      errors={errors}
      note={note}
      profileView={profileView}
    />
  );

  let diagnosisProgress = (
    <PrescribeDiagnosisProgress
      editable={editable}
      editHandler={editHandler}
      isCnaiEnabled={true}
      note={note}
      profileView={profileView}
    />
  );

  // see symptomTrendNotesMobile below — same rationale (isCnaiEnabled omitted/false here).
  let diagnosisProgressMobile = (
    <PrescribeDiagnosisProgress
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
    />
  );

  let symptomTrendNotes = (
    <GenericSectionNotes
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
      sectionKey="symptom_trend_notes"
      sectionLabel="Symptom Trends/Review"
      textareaProps={makeCnaiProps({
        format: "paragraph",
        id: "symptom-trend-notes",
        instructions: `Write in objective, third-person clinical language. Provide one concise sentence (25-30 words) describing the patient's symptom status and trend since the last visit. Include a brief direct client quote if a relevant one is available in the Transcript. Describe severity, duration, intensity, trend, and functional impact (e.g., sleep, daily activities, work, social engagement). Reference pre-session survey data (PHQ-9, GAD-7) if available. Flag any acute concerns (suicidal ideation, self-harm, homicidal ideation, crisis/ED visits) and note sobriety status with days free of use if substance use is discussed. End with: Severity Rating: X/10 (10 = worst). Example: Clt reports moderate-to-high anxiety increased over past week; sleep disruption, self-criticism, and worry impair functioning. Severity Rating: 7/10`,
        name: "Symptom Trends/Review",
        type: "string",
      })}
    />
  );

  // desktop and mobile render the same field simultaneously (CSS-toggled, not unmounted) —
  // tagging both would give the extension two elements sharing one cnai-template-field-id.
  // Only the desktop copy above carries cnai attrs; this one is DOM-identical but untagged.
  let symptomTrendNotesMobile = (
    <GenericSectionNotes
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
      sectionKey="symptom_trend_notes"
      sectionLabel="Symptom Trends/Review"
    />
  );

  let medicationImpactNote = (
    <GenericSectionNotes
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
      sectionKey="medication_impact_notes"
      sectionLabel="Medication Impact"
      textareaProps={makeCnaiProps({
        format: "paragraph",
        id: "medication-impact-notes",
        instructions: `Write a concise medication impact summary (1-4 sentences) based on the Transcript. Cover: each current psychiatric medication with name, dose, and frequency; whether it is continued, adjusted, added, or discontinued this visit; patient-reported benefit or tolerability if stated; medication reconciliation status if completed; and follow-up interval for medication management. Use standard clinical shorthand (QD, BID, PRN, QAM, HS, etc.). Do not reference the patient by name - use "the patient."`,
        name: "Medication Impact",
        type: "string",
      })}
    />
  );

  // see symptomTrendNotesMobile above — same rationale, kept untagged.
  let medicationImpactNoteMobile = (
    <GenericSectionNotes
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
      sectionKey="medication_impact_notes"
      sectionLabel="Medication Impact"
    />
  );

  let medicalInformationNotes = (
    <GenericSectionNotes
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
      renderExtra={() => {
        let prescriptionMonitoring = note.content.prescription_monitoring;

        return (
          <div className="ml-2">
            <input
              {...getAutomationDataAttr("medical-information-note-i-stop")}
              checked={!!prescriptionMonitoring.clicked}
              disabled={!editable}
              onChange={
                editable
                  ? (e) =>
                      editHandler("prescription_monitoring", {
                        ...prescriptionMonitoring,
                        clicked: e.target.checked,
                        last_viewed: e.target.checked
                          ? dayjs.utc().toISOString()
                          : prescriptionMonitoring.last_viewed,
                      })
                  : undefined
              }
              type="checkbox"
            />
            <span className="ml-2">{prescriptionMonitoring.type}</span>
          </div>
        );
      }}
      sectionKey="medical_information_notes"
      sectionLabel="Medical Information"
      textareaProps={makeCnaiProps({
        format: "paragraph",
        id: "medical-information-notes",
        instructions: `Write a structured medical background summary based on the Transcript. Include only information explicitly discussed or updated during this session. Cover (as applicable): allergies (NKDA or specific); past medical history; past surgical history; current non-psychiatric medications and supplements; family psychiatric or medical history; social history (substance use, living situation, occupation, relationship status). If no changes or updates were reported, write "No changes or updates reported." Use bullet lists under labeled sections (e.g., PAST MEDICAL HISTORY:). Do not reference the patient by name - use "the patient."`,
        name: "Medical Information",
        type: "string",
      })}
    />
  );

  // see symptomTrendNotesMobile above — same rationale, kept untagged (renderExtra/I-STOP
  // checkbox is intentionally not cnai-tagged either way, see ASD-1368).
  let medicalInformationNotesMobile = (
    <GenericSectionNotes
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
      renderExtra={() => {
        let prescriptionMonitoring = note.content.prescription_monitoring;

        return (
          <div className="ml-2">
            <input
              {...getAutomationDataAttr("medical-information-note-i-stop")}
              checked={!!prescriptionMonitoring.clicked}
              disabled={!editable}
              onChange={
                editable
                  ? (e) =>
                      editHandler("prescription_monitoring", {
                        ...prescriptionMonitoring,
                        clicked: e.target.checked,
                        last_viewed: e.target.checked
                          ? dayjs.utc().toISOString()
                          : prescriptionMonitoring.last_viewed,
                      })
                  : undefined
              }
              type="checkbox"
            />
            <span className="ml-2">{prescriptionMonitoring.type}</span>
          </div>
        );
      }}
      sectionKey="medical_information_notes"
      sectionLabel="Medical Information"
    />
  );

  let mentalStatus = (
    <BehavioralMentalStatus
      createEditView={createEditView}
      editable={editable}
      editHandler={editHandler}
      isCnaiEnabled={true}
      note={note}
      profileView={profileView}
      showTitle={true}
    />
  );

  // see symptomTrendNotesMobile above — same rationale (isCnaiEnabled omitted/false here).
  let mentalStatusMobile = (
    <BehavioralMentalStatus
      createEditView={createEditView}
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
      showTitle={true}
    />
  );

  let symptomTrends;

  if (note.content.last_behavioral_note) {
    symptomTrends = (
      <BehavioralSymptomTrend
        editable={false}
        editHandler={editHandler}
        note={note.content.last_behavioral_note}
        patientAnswersEditable={false}
        profileView={profileView}
        showProvider={true}
        smallerDimensions={true}
      />
    );
  } else {
    symptomTrends = (
      <CardSecondary className="mx-0 px-3 py-3 w-100">
        Patient has not had previous behavioral guided session for trends.
      </CardSecondary>
    );
  }

  let adherenceInterventions = (
    <GenericMultiselect
      editable={editable}
      editHandler={editHandler}
      isCheckboxes={true}
      labelClass=""
      note={note}
      profileView={profileView}
      sectionKey="adherence_interventions"
      sectionLabel="Adherence Interventions"
    />
  );

  let medicalProblems = (
    <ExpandingSelect
      className="m-3 grey-container"
      collapsible={false}
      disabled
      label="Medical Problems"
      labelClass="tw:font-bold clr-sec"
      options={note.content.medical_problems}
    />
  );

  let overallTrend = <IndividualSymptomTrend answer={note.content.symptom_trends.overall} />;

  let individualTrends = ["frequency", "effectiveness", "problems", "stop"]
    .filter((key) => note.content.medication_trends[key])
    .map((key) => (
      <IndividualSymptomTrend answer={note.content.medication_trends[key]} key={key} />
    ));

  let medicationProblems = (
    <ExpandingSelect
      className="m-3 grey-container"
      collapsible={false}
      disabled
      label="What makes it hard to take your medication?"
      labelClass="tw:font-bold clr-sec fs-13"
      options={note.content.medication_difficulties}
    />
  );

  let sideEffects = (
    <GenericMultiselect
      editable={editable}
      editHandler={editHandler}
      isCheckboxes={true}
      labelClass=""
      note={note}
      profileView={profileView}
      sectionKey="side_effects"
      sectionLabel="Side Effect(s)"
      // noneSelectedMessage='No side effects observed or reported.'
    />
  );

  let labFindings = (
    <PrescribeLabFindings
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
    />
  );

  let diagnosis = (
    <NotesDiagnosis
      editable={editable}
      editHandler={editHandler}
      errors={errors}
      note={note}
      profile={profile}
      profileView={profileView}
    />
  );

  let duration = !isCallManuallyLinked ? (
    <MemberProfileNotesDuration
      editable={editable}
      editHandler={editHandler}
      errors={errors}
      note={note}
      profileView={profileView}
    />
  ) : (
    <></>
  );

  let discharges = Object.hasOwn(note.content, "discharges") ? (
    <DischargeContent
      classes={{ card: editable ? "CardSeconday py-2" : null, colReadonly: "col-5" }}
      dischargeValues={note.content.discharges}
      editable={editable}
      setDischargeValues={(val, _key, nested) => editHandler("discharges", val, nested)}
      version="2"
    />
  ) : null;

  let bottomArea;

  if (createEditView) {
    bottomArea = (
      <div className="tw:flex tw:flex-col tw:gap-5">
        {diagnosis}
        {duration}
        {discharges}
      </div>
    );
  } else if (profileView) {
    bottomArea = (
      <>
        {diagnosis}
        {duration}
        {discharges}
      </>
    );
  }

  let otherCss = profileView ? "px-0 mx-0 w-100" : "m-auto";

  let desktopContent = (
    <div className="tw:hidden tw:md:flex tw:md:flex-col tw:md:gap-5">
      <div>{medications}</div>
      <div>{diagnosisProgress}</div>

      <div className={`row ${otherCss}`}>
        <div className="col-8 px-0">
          <div className="row px-0">
            <aside className="col-6 tw:flex tw:flex-col tw:gap-5">
              <div className={profileView ? "p-3 border-right-note border-bottom-note" : ""}>
                {symptomTrendNotes}
              </div>

              <div className={profileView ? "p-3 border-right-note border-bottom-note" : ""}>
                {medicationImpactNote}
              </div>

              <div className={profileView ? "p-3 border-right-note" : ""}>
                {medicalInformationNotes}
              </div>

              <div className={profileView ? "p-3 border-right-note" : ""}>{mentalStatus}</div>
            </aside>

            <div className="col-6">
              <div className={profileView ? "p-3 border-right-note" : ""}>{symptomTrends}</div>
              <div
                className={
                  profileView ? "p-3 border-top-note border-right-note border-bottom-note" : ""
                }
              >
                {adherenceInterventions}
              </div>
            </div>
          </div>

          <div className={profileView ? "p-3" : ""}>{bottomArea}</div>
        </div>

        <div className="col-4 px-0">
          <div className="row px-0">
            <div className="col-12">
              <div className={profileView ? "p-3 border-right-note border-bottom-note" : ""}>
                {medicalProblems}
              </div>

              <div className={profileView ? "p-3 border-right-note border-bottom-note" : ""}>
                {overallTrend}
              </div>

              <div
                className={
                  profileView ? "p-3 border-right-note border-left-note border-bottom-note" : ""
                }
              >
                {individualTrends}
              </div>

              <div
                className={
                  profileView ? "p-3 border-right-note border-left-note border-bottom-note" : ""
                }
              >
                {medicationProblems}
              </div>

              <div
                className={
                  profileView ? "p-3 border-right-note border-left-note border-bottom-note" : "px-3"
                }
              >
                {sideEffects}
              </div>

              <div
                className={
                  profileView ? "p-3 border-right-note border-left-note border-bottom-note" : ""
                }
              >
                {labFindings}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  let mobileContent = (
    <div className="d-block d-md-none d-lg-none">
      {medications}
      {diagnosisProgressMobile}
      {symptomTrendNotesMobile}
      {medicationImpactNoteMobile}
      {medicalInformationNotesMobile}
      {mentalStatusMobile}
      {symptomTrends}
      {adherenceInterventions}
      {medicalProblems}
      {overallTrend}
      {individualTrends}
      {medicalProblems}
      {sideEffects}
      {labFindings}
      {duration}
      {discharges}
      {bottomArea}
    </div>
  );

  return (
    <div
      className="tw:contents"
      {...makeCnaiTemplateProps({
        id: "prescribe-progress-note",
        name: "Prescribe Progress Note",
        type: "Individual Progress Note",
      })}
    >
      {desktopContent}
      {mobileContent}
    </div>
  );
}
